import React, { useEffect, useState } from "react";
import { FaUserTie, FaUserPlus, FaBriefcase, FaFileAlt } from "react-icons/fa";
import { BsFillPeopleFill } from "react-icons/bs";
import { AiOutlineFileDone } from "react-icons/ai";
import { UserCount } from "../../Redux/Slices/User";
import NotificationActionCard from "../Common/NotificationActionCard";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../../asset/new/css/admin-modern.css";

const AdminHome = () => {
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      const data = await UserCount();
      setCount(data);
    } catch (error) {
      console.error("Error fetching counts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const stats = [
    {
      icon: <BsFillPeopleFill />,
      label: "Total Employers",
      value: count?.employerCount || 0,
      color: "primary",
      subStats: []
    },
    {
      icon: <FaUserTie />,
      label: "Total Job Seekers",
      value: count?.jobseekerCount || 0,
      color: "success",
      subStats: [
        { label: "Resumes", value: count?.resumecount || 0 }
      ]
    },
    {
      icon: <FaBriefcase />,
      label: "Job Posts",
      value: count?.jobcount || 0,
      color: "info",
      subStats: []
    },
    {
      icon: <AiOutlineFileDone />,
      label: "Applied Jobs",
      value: count?.appliedjob || 0,
      color: "warning",
      subStats: []
    }
  ];

  // Generate trend data based on current counts
  const generateTrendData = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    const jobCount = count?.jobcount || 0;
    const appliedCount = count?.appliedjob || 0;
    
    return months.map((month, index) => ({
      month,
      jobs: Math.floor((jobCount / 6) * (index + 1) + Math.random() * 10),
      applications: Math.floor((appliedCount / 6) * (index + 1) + Math.random() * 15),
    }));
  };

  const generateUserData = () => {
    return [
      { name: "Employers", value: count?.employerCount || 0 },
      { name: "Job Seekers", value: count?.jobseekerCount || 0 },
    ];
  };

  const COLORS = ["#4893B3", "#0E9F6E", "#FF9500", "#F05252"];

  if (loading) {
    return (
      <>
        <div className="admin-grid admin-grid-cols-4 admin-gap-lg">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-stat-card">
              <div className="admin-skeleton" style={{ width: '56px', height: '56px', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--spacing-md)' }}></div>
              <div className="admin-skeleton" style={{ width: '60%', height: '14px', marginBottom: 'var(--spacing-sm)' }}></div>
              <div className="admin-skeleton" style={{ width: '80px', height: '32px' }}></div>
            </div>
          ))}
        </div>
        <div className="admin-grid admin-grid-cols-2 admin-gap-lg" style={{ marginTop: 'var(--spacing-2xl)' }}>
          {[1, 2].map((i) => (
            <div key={i} className="admin-card">
              <div className="admin-card-body">
                <div className="admin-skeleton" style={{ width: '40%', height: '24px', marginBottom: 'var(--spacing-lg)' }}></div>
                <div className="admin-skeleton" style={{ width: '100%', height: '300px', borderRadius: 'var(--radius-md)' }}></div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  const trendData = generateTrendData();
  const userData = generateUserData();

  return (
    <>
      <NotificationActionCard variant="test" />
      <div className="admin-grid admin-grid-cols-4 admin-gap-lg">
        {stats.map((stat, index) => (
          <div key={index} className="admin-stat-card">
            <div className={`admin-stat-card-icon ${stat.color}`}>
              {stat.icon}
            </div>
            <div className="admin-stat-card-label">{stat.label}</div>
            <div className="admin-stat-card-value">{stat.value}</div>
            {stat.subStats.length > 0 && (
              <div style={{ marginTop: 'var(--spacing-sm)' }}>
                {stat.subStats.map((sub, i) => (
                  <div key={i} style={{ 
                    fontSize: '0.875rem', 
                    color: 'var(--admin-text-secondary)',
                    marginTop: 'var(--spacing-xs)'
                  }}>
                    {sub.label}: <strong>{sub.value}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="admin-grid admin-grid-cols-2 admin-gap-lg" style={{ marginTop: 'var(--spacing-2xl)' }}>
        {/* Job Activity Trend */}
        <div className="admin-card">
          <div className="admin-card-body">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: 'var(--spacing-xl)'
            }}>
              <div>
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '600', 
                  marginBottom: 'var(--spacing-xs)',
                  color: 'var(--admin-text-primary)',
                  letterSpacing: '-0.01em'
                }}>
                  Job Activity Trends
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--admin-text-muted)',
                  margin: 0
                }}>
                  Monthly job posts and applications overview
                </p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="#6B7280"
                  style={{ fontSize: '0.875rem', fontWeight: '500' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis 
                  stroke="#6B7280"
                  style={{ fontSize: '0.875rem', fontWeight: '500' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    padding: '12px 16px'
                  }}
                  labelStyle={{
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#111827'
                  }}
                />
                <Legend 
                  wrapperStyle={{ 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    paddingTop: '16px'
                  }}
                  iconType="circle"
                />
                <Line 
                  type="monotone" 
                  dataKey="jobs" 
                  stroke="#4893B3" 
                  strokeWidth={3}
                  name="Job Posts"
                  dot={{ fill: '#4893B3', r: 5, strokeWidth: 2, stroke: 'white' }}
                  activeDot={{ r: 7, strokeWidth: 3 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="applications" 
                  stroke="#0E9F6E" 
                  strokeWidth={3}
                  name="Applications"
                  dot={{ fill: '#0E9F6E', r: 5, strokeWidth: 2, stroke: 'white' }}
                  activeDot={{ r: 7, strokeWidth: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Distribution */}
        <div className="admin-card">
          <div className="admin-card-body">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: 'var(--spacing-xl)'
            }}>
              <div>
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '600', 
                  marginBottom: 'var(--spacing-xs)',
                  color: 'var(--admin-text-primary)',
                  letterSpacing: '-0.01em'
                }}>
                  User Distribution
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--admin-text-muted)',
                  margin: 0
                }}>
                  Breakdown of platform users
                </p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={userData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  stroke="#6B7280"
                  style={{ fontSize: '0.875rem', fontWeight: '500' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis 
                  stroke="#6B7280"
                  style={{ fontSize: '0.875rem', fontWeight: '500' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    padding: '12px 16px'
                  }}
                  labelStyle={{
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#111827'
                  }}
                  cursor={{ fill: 'rgba(72, 147, 179, 0.05)' }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#4893B3"
                  radius={[12, 12, 0, 0]}
                  name="Users"
                  maxBarSize={80}
                >
                  {userData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminHome;
